import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CheckCircle, Clock, AlertCircle,
  Building2, Calendar, Pencil, X, Save, RotateCcw,
  LayoutList, Table2, ChevronLeft, ChevronRight,
  Eye, EyeOff, Users, Loader, MapPin, Play,
} from 'lucide-react';
import api from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { ClientReminder } from './components/ClientDetail/types';

type TimeFilter = 'all' | 'today' | 'week';
type StatusFilter = 'pending' | 'overdue' | 'completed' | 'snoozed' | 'all';
type VisibilityFilter = 'all' | 'for_me' | 'for_others';

interface ReminderWithClient extends ClientReminder {
  created_by_name?: string;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  overdue: 'En retard',
  completed: 'Terminé',
  snoozed: 'Reporté',
  cancelled: 'Annulé',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  overdue: 'bg-red-100 text-red-700',
  completed: 'bg-green-100 text-green-700',
  snoozed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
};

function toUtc(dt: string): string {
  return dt.endsWith('Z') || dt.includes('+') ? dt : dt + 'Z';
}

function fmt(dt: string | null | undefined): string {
  if (!dt) return '—';
  return new Date(toUtc(dt)).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function toInputDate(dt: string | null | undefined): string {
  if (!dt) return '';
  const d = new Date(toUtc(dt));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function toInputTime(dt: string | null | undefined): string {
  if (!dt) return '08:00';
  const d = new Date(toUtc(dt));
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const now = () => new Date();

function isOverdue(r: ReminderWithClient): boolean {
  return r.status === 'overdue';
}

function isToday(dt: string): boolean {
  const d = new Date(toUtc(dt));
  const t = now();
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
}

function isThisWeek(dt: string): boolean {
  const d = new Date(toUtc(dt));
  const t = now();
  const weekEnd = new Date(t);
  weekEnd.setDate(t.getDate() + 7);
  return d >= t && d < weekEnd;
}

// ─── Section helper ───────────────────────────────────────────────────────────
function Section({
  title, color, icon, items, children,
}: {
  title: string;
  color: string;
  icon: React.ReactNode;
  items: ReminderWithClient[];
  children: (r: ReminderWithClient) => React.ReactNode;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mb-6">
      <div className={`flex items-center gap-2 mb-3 px-1`}>
        {icon}
        <h2 className={`text-sm font-bold uppercase tracking-wider ${color}`}>{title}</h2>
        <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${color} bg-opacity-10`}>
          {items.length}
        </span>
      </div>
      <div className="space-y-2">{items.map(r => children(r))}</div>
    </div>
  );
}

export default function MyReminders() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  const isReadOnly = false;

  const [allReminders, setAllReminders] = useState<ReminderWithClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('all');

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('08:00');
  const [saving, setSaving] = useState(false);

  // Snooze state
  const [snoozingId, setSnoozingId] = useState<number | null>(null);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [snoozeTime, setSnoozeTime] = useState('08:00');
  const [snoozing, setSnoozing] = useState(false);

  // Visibility editing state
  const [editingVisibilityId, setEditingVisibilityId] = useState<number | null>(null);
  const [editVisibleTo, setEditVisibleTo] = useState<number[]>([]);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [users, setUsers] = useState<any[]>([]);           // per-client users for edit panel
  const [allUsers, setAllUsers] = useState<any[]>([]);      // global users for name display
  const [loadingUsers, setLoadingUsers] = useState(false);

  // View mode, grouping, city filter, and pagination
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');
  const [groupByCity, setGroupByCity] = useState(false);
  const [cityFilter, setCityFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Counts for stat cards (fetched separately since allReminders is paginated)
  const [statCounts, setStatCounts] = useState({
    pending: 0,
    overdue: 0,
    snoozed: 0,
    completed: 0,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (visibilityFilter !== 'all' && isAdmin) {
        params.append('visibility', visibilityFilter);
      }
      // Add date filtering for time filter
      if (timeFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        params.append('date_from', today);
        params.append('date_to', today);
      } else if (timeFilter === 'week') {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - daysFromMonday);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        params.append('date_from', weekStart.toISOString().split('T')[0]);
        params.append('date_to', weekEnd.toISOString().split('T')[0]);
      }
      params.append('page', currentPage.toString());
      params.append('page_size', itemsPerPage.toString());
      const url = `/reminders/mine?${params.toString()}`;
      const res = await api.get(url);
      if (res.data && typeof res.data === 'object' && 'items' in res.data) {
        setAllReminders(res.data.items || []);
        setTotalItems(res.data.total || 0);
        setTotalPages(res.data.total_pages || 1);
      } else {
        setAllReminders(Array.isArray(res.data) ? res.data : []);
        setTotalItems(Array.isArray(res.data) ? res.data.length : 0);
        setTotalPages(1);
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [statusFilter, timeFilter, visibilityFilter, isAdmin, currentPage]);

  // ── Fetch stat counts ─────────────────────────────────────────────────────
  const loadStatCounts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (visibilityFilter !== 'all' && isAdmin) {
        params.append('visibility', visibilityFilter);
      }
      const url = `/reminders/mine/count?${params.toString()}`;
      const res = await api.get(url);
      if (res.data) {
        setStatCounts({
          pending: res.data.pending || 0,
          overdue: res.data.overdue || 0,
          snoozed: res.data.snoozed || 0,
          completed: res.data.completed || 0,
        });
      }
    } catch { /* silent */ }
  }, [visibilityFilter, isAdmin]);

  useEffect(() => { load(); loadStatCounts(); }, [load, loadStatCounts]);

  // ── Counts for stat cards ────────────────────────────────────────────────
  const pendingCount = statCounts.pending;
  const overdueCount = statCounts.overdue;
  const snoozedCount = statCounts.snoozed;
  const completedCount = statCounts.completed;


  // ── Unique cities for filter dropdown ────────────────────────────────────
  const availableCities = Array.from(
    new Set(allReminders.map(r => r.client_city).filter(Boolean) as string[])
  ).sort((a, b) => a.localeCompare(b, 'fr'));

  // ── Filtered display list ─────────────────────────────────────────────────
  // Note: Backend already handles status and time/date filtering
  // We apply city filter client-side, then sort
  const cityFiltered = cityFilter === 'all'
    ? allReminders
    : cityFilter === '__none__'
      ? allReminders.filter(r => !r.client_city)
      : allReminders.filter(r => r.client_city === cityFilter);

  const sorted = [...cityFiltered].sort(
    (a, b) => new Date(a.reminder_datetime).getTime() - new Date(b.reminder_datetime).getTime()
  );

  // ── Sections (only when pending + no time filter) ─────────────────────────
  const showSections = statusFilter === 'pending' && timeFilter === 'all';
  const overdueItems = sorted.filter(r => isOverdue(r));
  const todayItems = sorted.filter(r => !isOverdue(r) && !!r.reminder_datetime && isToday(r.reminder_datetime));
  const weekItems = sorted.filter(r => !isOverdue(r) && !!r.reminder_datetime && !isToday(r.reminder_datetime) && isThisWeek(r.reminder_datetime));
  const laterItems = sorted.filter(r => !isOverdue(r) && !!r.reminder_datetime && !isToday(r.reminder_datetime) && !isThisWeek(r.reminder_datetime));

  // ── Actions ───────────────────────────────────────────────────────────────
  const toggleComplete = async (r: ReminderWithClient) => {
    try {
      await api.put(`/clients/${r.client_id}/reminders/${r.id}`, {
        status: r.status === 'completed' ? 'pending' : 'completed',
      });
      load();
    } catch { /* silent */ }
  };

  const startEdit = (r: ReminderWithClient) => {
    setEditingId(r.id);
    setEditContent(r.content || '');
    setEditDate(toInputDate(r.reminder_datetime));
    setEditTime(toInputTime(r.reminder_datetime));
  };

  const cancelEdit = () => { setEditingId(null); setEditContent(''); setEditDate(''); setEditTime('08:00'); };

  const saveEdit = async (r: ReminderWithClient) => {
    setSaving(true);
    try {
      const isoDatetime = editDate
        ? new Date(`${editDate}T${editTime || '08:00'}:00`).toISOString()
        : undefined;
      await api.put(`/clients/${r.client_id}/reminders/${r.id}`, {
        content: editContent,
        ...(isoDatetime ? { reminder_datetime: isoDatetime } : {}),
      });
      cancelEdit();
      load();
    } catch { /* silent */ } finally { setSaving(false); }
  };

  const doSnooze = async (r: ReminderWithClient) => {
    if (!snoozeDate) return;
    setSnoozing(true);
    try {
      const iso = new Date(`${snoozeDate}T${snoozeTime || '08:00'}:00`).toISOString();
      await api.post(`/clients/${r.client_id}/reminders/${r.id}/snooze`, { new_reminder_datetime: iso });
      setSnoozingId(null);
      setSnoozeDate('');
      setSnoozeTime('08:00');
      load();
    } catch { /* silent */ } finally { setSnoozing(false); }
  };

  const reactivate = async (r: ReminderWithClient) => {
    try {
      await api.put(`/clients/${r.client_id}/reminders/${r.id}`, { status: 'pending' });
      load();
      loadStatCounts();
    } catch { /* silent */ }
  };

  // ── Load all users for name resolution (admin only) ──────────────────────
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/reminders/users')
      .then(res => setAllUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setAllUsers([]));
  }, [isAdmin]);

  // ── Visibility editing functions ─────────────────────────────────────────
  const startEditVisibility = (r: ReminderWithClient) => {
    setEditingVisibilityId(r.id);
    setEditVisibleTo(r.visible_to_user_ids || []);
    // Load users scoped to this reminder's client
    setLoadingUsers(true);
    api.get(`/reminders/users?client_id=${r.client_id}`)
      .then(res => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  };

  const cancelEditVisibility = () => {
    setEditingVisibilityId(null);
    setEditVisibleTo([]);
  };

  const saveVisibility = async (r: ReminderWithClient) => {
    setSavingVisibility(true);
    try {
      const ids = editVisibleTo.join(',');
      await api.put(`/reminders/${r.id}/visibility?visible_to=${ids}`);
      cancelEditVisibility();
      load();
      loadStatCounts();
    } catch { /* silent */ } finally { setSavingVisibility(false); }
  };

  const toggleUserVisibility = (userId: number) => {
    setEditVisibleTo(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  // ── Card click handler ────────────────────────────────────────────────────
  const clickCard = (sf: StatusFilter, tf: TimeFilter = 'all') => {
    setStatusFilter(sf);
    setTimeFilter(tf);
    setCurrentPage(1);
  };

  // ── Helper: resolve user name from ID ────────────────────────────────────
  const getUserName = (userId: number): string => {
    if (currentUser && userId === currentUser.id) return 'Moi';
    const u = allUsers.find(u => u.id === userId) || users.find(u => u.id === userId);
    if (u) {
      const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
      return name || u.email;
    }
    return `User #${userId}`;
  };

  // ── Table row renderer ──────────────────────────────────────────────────
  const renderTableRow = (reminder: ReminderWithClient) => (
    <tr key={reminder.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
      <td className="px-3 py-2 sm:px-4 sm:py-3">
        <button
          onClick={() => navigate(`/achat/clients/${reminder.client_id}`)}
          className="font-medium text-slate-900 dark:text-slate-100 hover:text-orange-600 dark:hover:text-orange-400 truncate max-w-[120px] sm:max-w-[150px] block"
          title={reminder.client_company_name || `Client #${reminder.client_id}`}
        >
          {reminder.client_company_name || `Client #${reminder.client_id}`}
        </button>
        <div className="sm:hidden text-xs text-slate-500 dark:text-slate-400 mt-1 truncate max-w-[150px]">
          {reminder.content}
        </div>
      </td>
      <td className="hidden md:table-cell px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap">
        {reminder.client_city ? (
          <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
            <MapPin className="w-3 h-3" />{reminder.client_city}
          </span>
        ) : (
          <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
        )}
      </td>
      <td className="hidden sm:table-cell px-3 py-2 sm:px-4 sm:py-3 max-w-xs truncate text-slate-600 dark:text-slate-400">{reminder.content}</td>
      <td className="px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap">
        <span className={isOverdue(reminder) ? 'text-red-600 dark:text-red-400 font-medium text-xs sm:text-sm' : 'text-slate-600 dark:text-slate-400 text-xs sm:text-sm'}>
          {fmt(reminder.reminder_datetime)}
        </span>
      </td>
      <td className="px-3 py-2 sm:px-4 sm:py-3">
        <span className={`inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase ${STATUS_COLORS[reminder.status] || STATUS_COLORS.pending}`}>
          {STATUS_LABELS[reminder.status] || reminder.status}
        </span>
      </td>
      <td className="hidden lg:table-cell px-3 py-2 sm:px-4 sm:py-3 text-slate-600 dark:text-slate-400 text-xs sm:text-sm whitespace-nowrap">{reminder.created_by_name || `User #${reminder.created_by}`}</td>
      <td className="px-3 py-2 sm:px-4 sm:py-3">
        <div className="flex items-center justify-center gap-0.5 sm:gap-1">
          <button
            onClick={() => navigate(`/achat/clients/${reminder.client_id}?tab=notes`)}
            title="Accéder à la fiche client"
            className="p-1 sm:p-1.5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg"
          >
            <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
          {!isReadOnly && (
            <button
              onClick={() => toggleComplete(reminder)}
              title={reminder.status === 'completed' ? 'Remettre en attente' : 'Marquer comme fait'}
              className={`p-1 sm:p-1.5 rounded-lg ${reminder.status === 'completed' ? 'text-green-600 bg-green-50 dark:bg-green-900/20' : 'text-slate-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'}`}
            >
              <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  // ── Reminder card renderer ────────────────────────────────────────────────
  const renderReminder = (reminder: ReminderWithClient) => {
    const overdue = isOverdue(reminder);
    const isEditing = editingId === reminder.id;

    return (
      <div
        key={reminder.id}
        className={`bg-white dark:bg-slate-900 rounded-xl border p-4 transition-colors ${overdue ? 'border-red-300 dark:border-red-700 bg-red-50/30 dark:bg-red-900/10' : 'border-slate-200 dark:border-slate-700'
          }`}
      >
        {isEditing ? (
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-300">
              <Building2 className="w-3.5 h-3.5" />
              {reminder.client_company_name || `Client #${reminder.client_id}`}
            </div>
            <textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
              placeholder="Contenu du rappel..."
            />
            <div className="flex items-center gap-2 flex-wrap">
              <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
              <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)}
                className="flex-1 min-w-0 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-500" />
              <input type="time" value={editTime} onChange={e => setEditTime(e.target.value)}
                className="w-28 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-500" />
              <button onClick={cancelEdit} className="flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 hover:text-slate-900 transition-colors">
                <X className="w-3.5 h-3.5" /> Annuler
              </button>
              <button onClick={() => saveEdit(reminder)} disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors">
                <Save className="w-3.5 h-3.5" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              {/* Date + heure */}
              <div className={`flex items-center gap-1.5 text-xs font-semibold mb-1.5 ${overdue ? 'text-red-600 dark:text-red-400' :
                reminder.status === 'snoozed' ? 'text-blue-600 dark:text-blue-400' :
                  'text-slate-500 dark:text-slate-400'
                }`}>
                <Calendar className="w-3.5 h-3.5 shrink-0" />
                {fmt(reminder.reminder_datetime)}
                {overdue && <span className="ml-1 flex items-center gap-1 text-red-600 dark:text-red-400 font-bold"><AlertCircle className="w-3.5 h-3.5" /> En retard</span>}
                {reminder.status === 'snoozed' && <span className="ml-1 flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Reporté</span>}
              </div>

              {/* Nom du client + ville */}
              <button
                onClick={() => navigate(`/achat/clients/${reminder.client_id}`)}
                className="flex items-center gap-1.5 text-sm font-bold text-slate-900 dark:text-slate-100 hover:text-orange-600 dark:hover:text-orange-400 transition-colors mb-1.5"
              >
                <Building2 className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                <span className="truncate">{reminder.client_company_name || `Client #${reminder.client_id}`}</span>
                {reminder.client_city && (
                  <span className="flex items-center gap-0.5 text-xs font-medium text-slate-400 dark:text-slate-500 ml-1">
                    <MapPin className="w-3 h-3" />{reminder.client_city}
                  </span>
                )}
              </button>

              {/* Commentaire */}
              {reminder.content && (
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-2">
                  {reminder.content}
                </p>
              )}

              {/* Badge statut + meta */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_COLORS[reminder.status] || STATUS_COLORS.pending}`}>
                  {STATUS_LABELS[reminder.status] || reminder.status}
                </span>
                {reminder.status === 'completed' && reminder.completed_at && (
                  <span className="text-[10px] text-slate-400">
                    Traité le {new Date(reminder.completed_at).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </div>

              {/* Créé par + Visible pour */}
              {isAdmin && (
                <div className="flex items-center gap-3 flex-wrap mt-1.5 text-[11px] text-slate-400 dark:text-slate-500">
                  {reminder.created_by_name && (
                    <span className="flex items-center gap-1">
                      <Pencil className="w-3 h-3" />
                      <span className="font-medium">Créé par :</span> {reminder.created_by === currentUser?.id ? 'Moi' : reminder.created_by_name}
                    </span>
                  )}
                  {reminder.visible_to_user_ids && reminder.visible_to_user_ids.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      <span className="font-medium">Visible pour :</span>
                      {reminder.visible_to_user_ids.map(id => getUserName(id)).join(', ')}
                    </span>
                  )}
                  {(!reminder.visible_to_user_ids || reminder.visible_to_user_ids.length === 0) && (
                    <span className="flex items-center gap-1 text-amber-500">
                      <EyeOff className="w-3 h-3" />
                      Aucune visibilité définie
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col items-center gap-1 shrink-0">
              {/* Accéder à la fiche client */}
              <button
                onClick={() => navigate(`/achat/clients/${reminder.client_id}?tab=notes`)}
                title="Accéder à la fiche client"
                className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors"
              >
                <Building2 className="w-4 h-4" />
              </button>

              {/* Marquer comme fait — masqué pour admin/super_admin */}
              {!isReadOnly && (
                <button
                  onClick={() => toggleComplete(reminder)}
                  title={reminder.status === 'completed' ? 'Remettre en attente' : 'Marquer comme fait'}
                  className={`p-2 rounded-lg transition-colors ${reminder.status === 'completed'
                    ? 'text-green-600 bg-green-50 dark:bg-green-900/20'
                    : 'text-slate-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                    }`}
                >
                  <CheckCircle className="w-4 h-4" />
                </button>
              )}

              {/* Reporter — masqué pour admin/super_admin et rappels terminés */}
              {!isReadOnly && reminder.status !== 'completed' && reminder.status !== 'snoozed' && (
                <button
                  onClick={() => { setSnoozingId(reminder.id); setSnoozeDate(''); setSnoozeTime('08:00'); }}
                  title="Reporter à une autre date"
                  className={`p-2 rounded-lg transition-colors ${snoozingId === reminder.id
                    ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/20'
                    : 'text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                    }`}
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}

              {/* Réactiver — uniquement pour les rappels reportés */}
              {!isReadOnly && reminder.status === 'snoozed' && (
                <button
                  onClick={() => reactivate(reminder)}
                  title="Réactiver (remettre en attente)"
                  className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
                >
                  <Play className="w-4 h-4" />
                </button>
              )}

              {/* Modifier */}
              {!isReadOnly && reminder.status !== 'completed' && (
                <button
                  onClick={() => startEdit(reminder)}
                  title="Modifier le rappel"
                  className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}

              {/* Modifier visibilité — admin only */}
              {isAdmin && (
                <button
                  onClick={() => editingVisibilityId === reminder.id ? cancelEditVisibility() : startEditVisibility(reminder)}
                  title="Modifier la visibilité"
                  className={`p-2 rounded-lg transition-colors ${editingVisibilityId === reminder.id
                    ? 'text-purple-600 bg-purple-50 dark:bg-purple-900/20'
                    : 'text-slate-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                    }`}
                >
                  <Users className="w-4 h-4" />
                </button>
              )}

            </div>
          </div>
        )}

        {/* Inline snooze panel */}
        {snoozingId === reminder.id && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-2">Reporter à :</p>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="date"
                value={snoozeDate}
                onChange={e => setSnoozeDate(e.target.value)}
                className="flex-1 min-w-0 rounded-lg border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-800 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="time"
                value={snoozeTime}
                onChange={e => setSnoozeTime(e.target.value)}
                className="w-28 rounded-lg border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-800 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => { setSnoozingId(null); setSnoozeDate(''); setSnoozeTime('08:00'); }}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg border border-slate-200 dark:border-slate-700 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => doSnooze(reminder)}
                disabled={!snoozeDate || snoozing}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                {snoozing ? 'Reporter…' : 'Confirmer'}
              </button>
            </div>
          </div>
        )}

        {/* Inline visibility edit panel */}
        {editingVisibilityId === reminder.id && (
          <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-lg">
            <p className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-2 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Visible pour :
            </p>
            {loadingUsers ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><Loader className="w-4 h-4 animate-spin" /> Chargement…</div>
            ) : users.length === 0 ? (
              <p className="text-sm text-slate-400">Aucun utilisateur disponible</p>
            ) : (
              <div className="max-h-48 overflow-y-auto border rounded-lg dark:border-slate-600 divide-y divide-slate-100 dark:divide-slate-700 bg-white dark:bg-slate-800 mb-2">
                {users.map(u => {
                  const checked = editVisibleTo.includes(u.id);
                  return (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/10">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleUserVisibility(u.id)}
                        className="accent-purple-500"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300 flex-1">
                        {currentUser && u.id === currentUser.id ? 'Moi' :
                          ((u.first_name || u.last_name) ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : u.email)}
                      </span>
                      {u.role && <span className="text-[10px] text-slate-400 uppercase">{u.role}</span>}
                    </label>
                  );
                })}
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={cancelEditVisibility}
                className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => saveVisibility(reminder)}
                disabled={savingVisibility}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {savingVisibility ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5" /> Enregistrer</>}
              </button>
            </div>
          </div>
        )}

      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Bell className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Mes Rappels</h1>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 ml-12">Synthèse de tous vos rappels clients</p>
        </div>
      </div>

      {/* Visibility switch — admin/super_admin only */}
      {isAdmin && (
        <div className="flex items-center gap-1 mb-5 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl w-fit">
          {([
            { key: 'all' as VisibilityFilter, label: 'Tous', icon: <Eye className="w-3.5 h-3.5" /> },
            { key: 'for_me' as VisibilityFilter, label: 'Pour moi', icon: <Eye className="w-3.5 h-3.5" /> },
            { key: 'for_others' as VisibilityFilter, label: 'Pour les autres', icon: <EyeOff className="w-3.5 h-3.5" /> },
          ]).map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => { setVisibilityFilter(key); setCurrentPage(1); }}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${visibilityFilter === key
                ? 'bg-white dark:bg-slate-700 text-orange-600 dark:text-orange-400 shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
            >
              {icon}
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Stat cards — clickable filters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {/* EN ATTENTE */}
        <button
          onClick={() => clickCard('pending', 'all')}
          className={`text-left rounded-xl border p-4 transition-all hover:shadow-md ${statusFilter === 'pending' && timeFilter === 'all'
            ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 ring-2 ring-amber-400/30'
            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700'
            }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-500" />
            <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">En attente</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{pendingCount}</p>
        </button>

        {/* EN RETARD */}
        <button
          onClick={() => clickCard('overdue', 'all')}
          className={`text-left rounded-xl border p-4 transition-all hover:shadow-md ${statusFilter === 'overdue'
            ? 'border-red-400 bg-red-50 dark:bg-red-900/20 ring-2 ring-red-400/30'
            : overdueCount > 0
              ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
              : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700'
            }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className={`w-4 h-4 ${overdueCount > 0 ? 'text-red-500' : 'text-slate-400'}`} />
            <span className={`text-[11px] font-semibold uppercase tracking-wider ${overdueCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'}`}>En retard</span>
          </div>
          <p className={`text-2xl font-bold ${overdueCount > 0 ? 'text-red-700 dark:text-red-300' : 'text-slate-900 dark:text-slate-100'}`}>{overdueCount}</p>
        </button>

        {/* REPORTÉS */}
        <button
          onClick={() => clickCard('snoozed', 'all')}
          className={`text-left rounded-xl border p-4 transition-all hover:shadow-md ${statusFilter === 'snoozed'
            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-400/30'
            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700'
            }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <RotateCcw className="w-4 h-4 text-blue-500" />
            <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Reportés</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{snoozedCount}</p>
        </button>

        {/* TERMINÉS */}
        <button
          onClick={() => clickCard('completed', 'all')}
          className={`text-left rounded-xl border p-4 transition-all hover:shadow-md ${statusFilter === 'completed'
            ? 'border-green-400 bg-green-50 dark:bg-green-900/20 ring-2 ring-green-400/30'
            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700'
            }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Terminés</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{completedCount}</p>
        </button>
      </div>

      {/* Time filter pills — only relevant for pending */}
      {(statusFilter === 'pending' || statusFilter === 'all') && (
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          {([
            { key: 'all', label: 'Tous' },
            { key: 'today', label: "Aujourd'hui" },
            { key: 'week', label: 'Cette semaine' },
          ] as { key: TimeFilter; label: string }[]).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTimeFilter(key)}
              className={`px-4 py-1.5 text-sm font-medium rounded-full border transition-colors ${timeFilter === key
                ? 'bg-orange-600 text-white border-orange-600'
                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-orange-400 hover:text-orange-600'
                }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Snoozed info banner */}
      {statusFilter === 'snoozed' && (
        <div className="mb-5 flex items-start gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm text-blue-700 dark:text-blue-300">
          <RotateCcw className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Un rappel <strong>Reporté</strong> a été décalé à une date ultérieure. Il redeviendra actif à la nouvelle date fixée.</span>
        </div>
      )}

      {/* View toggle and count */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {cityFilter !== 'all' ? `${sorted.length} / ${totalItems}` : totalItems} rappel{(cityFilter !== 'all' ? sorted.length : totalItems) > 1 ? 's' : ''}
          {cityFilter !== 'all' && <span className="ml-1 text-teal-600 dark:text-teal-400 font-medium">({cityFilter === '__none__' ? 'sans ville' : cityFilter})</span>}
          {totalPages > 1 && ` • Page ${currentPage}/${totalPages}`}
        </span>
        <div className="flex items-center gap-2">
          {/* City filter dropdown */}
          <div className="relative">
            <select
              value={cityFilter}
              onChange={e => { setCityFilter(e.target.value); setCurrentPage(1); }}
              className={`appearance-none pl-7 pr-8 py-1.5 text-sm font-medium rounded-lg border transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500 ${cityFilter !== 'all'
                ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-400 text-teal-700 dark:text-teal-300'
                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                }`}
            >
              <option value="all">Toutes les villes</option>
              {availableCities.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
              <option value="__none__">Sans ville</option>
            </select>
            <MapPin className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          <button
            onClick={() => setGroupByCity(g => !g)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${groupByCity
              ? 'bg-teal-600 text-white border-teal-600'
              : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-teal-400 hover:text-teal-600'
              }`}
            title="Regrouper par ville"
          >
            <MapPin className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Par ville</span>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'list'
              ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100'
              : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
              }`}
            title="Vue liste"
          >
            <LayoutList className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'table'
              ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100'
              : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
              }`}
            title="Vue tableau"
          >
            <Table2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
          <Bell className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400 font-medium">Aucun rappel</p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
            {statusFilter === 'pending' && timeFilter === 'all' ? 'Tous vos rappels sont traités 🎉' : 'Aucun rappel dans cette catégorie'}
          </p>
        </div>
      ) : viewMode === 'table' ? (
        /* ── Table view ── */
        (() => {
          const colCount = 7; // Client, Ville, Contenu, Date, Statut, Créé par, Actions
          const tableRows: React.ReactNode[] = [];
          if (groupByCity) {
            const cityMap = new Map<string, ReminderWithClient[]>();
            for (const r of sorted) {
              const city = r.client_city || 'Ville non renseignée';
              if (!cityMap.has(city)) cityMap.set(city, []);
              cityMap.get(city)!.push(r);
            }
            const cities = [...cityMap.keys()].sort((a, b) =>
              a === 'Ville non renseignée' ? 1 : b === 'Ville non renseignée' ? -1 : a.localeCompare(b, 'fr')
            );
            for (const city of cities) {
              tableRows.push(
                <tr key={`city-${city}`} className="bg-teal-50 dark:bg-teal-900/20">
                  <td colSpan={colCount} className="px-3 py-2 sm:px-4 sm:py-2.5">
                    <div className="flex items-center gap-2">
                      <MapPin className={`w-3.5 h-3.5 ${city === 'Ville non renseignée' ? 'text-slate-400' : 'text-teal-600 dark:text-teal-400'}`} />
                      <span className={`text-xs font-bold uppercase tracking-wider ${city === 'Ville non renseignée' ? 'text-slate-400' : 'text-teal-700 dark:text-teal-300'}`}>
                        {city}
                      </span>
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400">
                        {cityMap.get(city)!.length}
                      </span>
                    </div>
                  </td>
                </tr>
              );
              for (const reminder of cityMap.get(city)!) {
                tableRows.push(renderTableRow(reminder));
              }
            }
          } else {
            for (const reminder of sorted) {
              tableRows.push(renderTableRow(reminder));
            }
          }
          return (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">Client</th>
                      <th className="hidden md:table-cell px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">Ville</th>
                      <th className="hidden sm:table-cell px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Contenu</th>
                      <th className="px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">Date</th>
                      <th className="px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">Statut</th>
                      <th className="hidden lg:table-cell px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">Créé par</th>
                      <th className="px-3 py-2 sm:px-4 sm:py-3 text-center font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {tableRows}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()
      ) : groupByCity ? (
        /* ── Grouped by city ── */
        (() => {
          const cityMap = new Map<string, ReminderWithClient[]>();
          for (const r of sorted) {
            const city = r.client_city || 'Ville non renseignée';
            if (!cityMap.has(city)) cityMap.set(city, []);
            cityMap.get(city)!.push(r);
          }
          const cities = [...cityMap.keys()].sort((a, b) =>
            a === 'Ville non renseignée' ? 1 : b === 'Ville non renseignée' ? -1 : a.localeCompare(b, 'fr')
          );
          return (
            <div className="space-y-6">
              {cities.map(city => (
                <div key={city}>
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <MapPin className={`w-4 h-4 ${city === 'Ville non renseignée' ? 'text-slate-400' : 'text-teal-500'}`} />
                    <h2 className={`text-sm font-bold uppercase tracking-wider ${city === 'Ville non renseignée' ? 'text-slate-400' : 'text-teal-600 dark:text-teal-400'}`}>
                      {city}
                    </h2>
                    <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20">
                      {cityMap.get(city)!.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {cityMap.get(city)!.map(r => renderReminder(r))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      ) : showSections ? (
        /* ── Sectioned view (pending + no time filter) ── */
        <>
          <Section title="En retard" color="text-red-600 dark:text-red-400" icon={<AlertCircle className="w-4 h-4 text-red-500" />} items={overdueItems}>
            {renderReminder}
          </Section>
          <Section title="Aujourd'hui" color="text-orange-600 dark:text-orange-400" icon={<Clock className="w-4 h-4 text-orange-500" />} items={todayItems}>
            {renderReminder}
          </Section>
          <Section title="Cette semaine" color="text-amber-600 dark:text-amber-400" icon={<Calendar className="w-4 h-4 text-amber-500" />} items={weekItems}>
            {renderReminder}
          </Section>
          <Section title="Plus tard" color="text-slate-500 dark:text-slate-400" icon={<Calendar className="w-4 h-4 text-slate-400" />} items={laterItems}>
            {renderReminder}
          </Section>
        </>
      ) : (
        /* ── Flat list (all other filters) ── */
        <div className="space-y-2">{sorted.map((r: ReminderWithClient) => renderReminder(r))}</div>
      )}

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-slate-600 dark:text-slate-400">
            Page {currentPage} sur {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
